import { Menu } from "lucide-react"
import type { MouseEvent } from "react"
import { useState } from "react"
import { Link } from "@tanstack/react-router"
import Logo from "@/ui/components/Logo"
import { Sheet, SheetContent, SheetTrigger } from "@/ui/components/ui/sheet"
import { IconButton } from "@/ui/composed"
import { scrollTopOnClick } from "@/utils"
import { ButtonLink, useLocale } from "@/base"
import { useAuth } from "@/auth"
import { LanguageSwitcher } from "@/profile"
import { useT } from "@/i18n"

function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useT("content")
  const { isAuthenticated } = useAuth()
  const locale = useLocale()

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    scrollTopOnClick(e)
    onNavigate?.()
  }

  return (
    <>
      <ButtonLink
        to="/$locale"
        params={{ locale }}
        hash="intro"
        variant="ghost"
        onClick={onNavigate}
      >
        {t("intro")}
      </ButtonLink>
      <LanguageSwitcher />
      {isAuthenticated ? (
        <ButtonLink className="ml-2" to="/dashboard" onClick={handleClick}>
          {t("common:nav.dashboard")}
        </ButtonLink>
      ) : (
        <ButtonLink
          className="ml-2"
          to="/$locale/auth"
          params={{ locale }}
          onClick={handleClick}
        >
          {t("getStarted")}
        </ButtonLink>
      )}
    </>
  )
}

export default function Nav() {
  const [open, setOpen] = useState(false)
  const locale = useLocale()

  return (
    <nav className="sticky top-0 z-50 border-b border-border/40 bg-background/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-250 items-center justify-between px-4 py-2">
        <Link to="/$locale" params={{ locale }} onClick={scrollTopOnClick}>
          <Logo size={28} className="my-1" />
        </Link>
        {/* Desktop nav */}
        <div className="hidden items-center gap-1 text-muted-foreground sm:flex">
          <NavLinks />
        </div>
        {/* Mobile hamburger */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger
            render={<IconButton variant="ghost" icon={<Menu />} />}
            className="sm:hidden"
          />
          <SheetContent side="right" className="pt-12" showCloseButton>
            <div className="flex flex-col gap-1 px-2 text-muted-foreground">
              <NavLinks
                onNavigate={() => {
                  setOpen(false)
                }}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  )
}
